import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AddHeader } from "../../components/AddHeader";
import { ExerciseAddLayout } from "./ExerciseAddLayout";
import { addexercise } from "../../components/funtionsBD/Mainservice";
import { fetchGroupSmartRoster } from "../../components/funtionsBD/digipingSmartService";
import Loader from "../../components/Loading/Loader";

function playerExerciseStatus(player) {
  const ex = player?.personalized_exercise;
  const status = String(ex?.status || "").toLowerCase();
  if (status === "assigned" && ex?.title) return "assigned";
  if (ex?.title && (status === "pending_coach" || status === "proposed")) return "pending";
  return "missing";
}

export function PersonalizedTrainingSlot() {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;
  const { t, i18n } = useTranslation();
  const lang = (localStorage.getItem("i18nextLng") || i18n.language || "en").split("-")[0];

  const [duration, setDuration] = useState(10);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data?.sessionid) {
      navigate("/SelectExerciseType", { replace: true });
    }
  }, [data?.sessionid, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const roster = await fetchGroupSmartRoster({
          grId: data?.grpid || null,
          lang,
        });
        if (cancelled) return;
        const list = roster?.players || [];
        const filtered = data?.grpid
          ? list.filter((p) => (p.gr_ids || []).map(String).includes(String(data.grpid)))
          : list;
        setPlayers(filtered.length ? filtered : list);
      } catch (err) {
        if (!cancelled) setError(err?.message || "");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.grpid, lang]);

  const counts = useMemo(() => {
    const assigned = players.filter((p) => playerExerciseStatus(p) === "assigned").length;
    return { assigned, total: players.length };
  }, [players]);

  const handleAdd = async () => {
    if (!data?.sessionid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await addexercise({
        ts_id: data.sessionid,
        allow_duration_exceed: true,
        tsg_id:
          data.tsg_id ||
          (typeof sessionStorage !== "undefined"
            ? sessionStorage.getItem("planning_tsg_id")
            : null),
        te_id: data.te_id || undefined,
        ex_ids: [
          {
            personalized_slot: true,
            duration: Number(duration) > 0 ? Number(duration) : 10,
            steps: "1",
          },
        ],
      });
      navigate("/planning");
    } catch (err) {
      setError(err?.message || t("general") || "Failed to add exercise");
      setSubmitting(false);
    }
  };

  return (
    <ExerciseAddLayout>
      <AddHeader title={t("addex")} />
      <section className="exadd-hero">
        <p className="exadd-kicker">{data?.type || t("personalizedSlotTitle")}</p>
        <p className="exadd-lead">
          {t(
            "personalizedSlotLead",
            "Each player runs the exercise Pingflo assigned for their primary SMART objective, after you validate it."
          )}
        </p>
      </section>

      <div className="exercise_session_div exadd-choices" style={{ display: "block" }}>
        <label className="exadd-lead" htmlFor="personalized-slot-duration">
          {t("personalizedSlotDuration", "Block duration (minutes)")}
        </label>
        <input
          id="personalized-slot-duration"
          className="exadd-duration-field"
          type="number"
          min={1}
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />

        {loading ? <Loader /> : null}

        {!loading && players.length === 0 ? (
          <p className="exadd-lead">
            {t(
              "personalizedSlotEmptyRoster",
              "No players in this group yet. The block will use each player's validated exercise once they are in the session."
            )}
          </p>
        ) : null}

        {!loading && players.length > 0 ? (
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
            {players.map((player) => {
              const kind = playerExerciseStatus(player);
              const title = player.personalized_exercise?.title;
              const focus = player.primary_focus;
              let line = t(
                "personalizedSlotMissing",
                "No validated exercise for the primary objective yet"
              );
              if (kind === "assigned") {
                line = t("personalizedSlotWillRun", {
                  title,
                  defaultValue: "Will run: {{title}}",
                });
              } else if (kind === "pending") {
                line = t("personalizedSlotAwaitingValidation", {
                  title,
                  defaultValue: "Awaiting your validation: {{title}}",
                });
              }
              return (
                <li key={player.user_id || player.player_id} style={{ marginBottom: 10 }}>
                  <strong>{player.name}</strong>
                  {focus ? <span> · {focus}</span> : null}
                  <div>{line}</div>
                </li>
              );
            })}
          </ul>
        ) : null}

        {!loading && counts.total > 0 ? (
          <p className="exadd-lead">
            {t("personalizedSlotReadyCount", {
              assigned: counts.assigned,
              total: counts.total,
              defaultValue: "{{assigned}} of {{total}} players have a validated exercise.",
            })}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="exadd-lead">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          className="expage-nav__add-btn"
          onClick={handleAdd}
          disabled={submitting || !data?.sessionid}
        >
          {submitting
            ? t("liveTrainingAddingExercise", "Adding…")
            : t("personalizedSlotAdd", "Add personalized block")}
        </button>
      </div>
    </ExerciseAddLayout>
  );
}

export default PersonalizedTrainingSlot;
